import { Box, ButtonBase, Fade, Grow, Theme, useMediaQuery } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import React from 'react';
import DrawerNav from './DrawerNav';
import useHeaderStyles from './header.style';
import { TabsData } from './HeaderUtils';
import { SupportMenu } from './SupportMenu';
import { UserTab } from './UserTab';
import { EngagementEvents, logKariusEvent } from '../Analytics';
import KariusK from '../assets/images/svg/KariusK';
import KariusLogoBlack from '../assets/images/svg/KariusLogoBlack';
import { Resources } from '../auth/authTypes';
import { checkAccess } from '../auth/checkAccess';
import { getThemeFontColor } from '../theme/header.theme';
import { themeSelector } from '../theme/ThemeSelector';

function a11yProps(index: any) {
  return {
    id: `nav-tab-${index}`,
    'aria-controls': `nav-tabpanel-${index}`,
  };
}

interface HeaderProps {
  selectedTab?: Resources;
}

export default function Header({ selectedTab }: HeaderProps) {
  const matchedSm = useMediaQuery<Theme>((theme) => {
    return theme.breakpoints.down('md');
  });

  return (
    <AppBar position='sticky' id='header-container'>
      <Fade in={matchedSm} timeout={300} unmountOnExit appear={false}>
        <DrawerNav selectedTab={selectedTab} />
      </Fade>
      <Fade in={!matchedSm} timeout={300} unmountOnExit>
        <Tabs
          value={0}
          aria-label='nav tabs example'
          TabIndicatorProps={{
            style: {
              display: 'flex',
            },
          }}
        >
          <LogoButton />
          <TabLink resourceName={Resources.eTRF} selectedTab={selectedTab} />
          <TabLink resourceName={Resources.ordersListView} selectedTab={selectedTab} />
          <TabLink resourceName={Resources.kitOrdering} selectedTab={selectedTab} />
          <TabLink resourceName={Resources.users} selectedTab={selectedTab} />
          <SupportMenu />
          <UserTab />
        </Tabs>
      </Fade>
    </AppBar>
  );
}

const LogoButton = function LogoButton() {
  const styles = useHeaderStyles();
  const matchesMd = useMediaQuery<Theme>((theme) => {
    return theme.breakpoints.down('lg');
  });
  return (
    <Link href='/' passHref={true}>
      <ButtonBase css={styles.logoContainer}>
        <Fade in={matchesMd} timeout={matchesMd ? 1500 : 800} appear={true} mountOnEnter={true}>
          <KariusK css={styles.headerK} fill={getThemeFontColor()} />
        </Fade>
        <Fade in={!matchesMd} timeout={!matchesMd ? 1500 : 800} appear={true} mountOnEnter={true}>
          {themeSelector({
            white: <KariusLogoBlack css={styles.headerlogo} />,
            blue: <img src='/KariusLogoLight.png' css={styles.headerLogoLight} alt='' />,
          })}
        </Fade>
      </ButtonBase>
    </Link>
  );
};

interface TabLinkProps {
  resourceName: Resources;
  selectedTab?: Resources;
}

export function TabLink({ resourceName, selectedTab }: TabLinkProps) {
  const styles = useHeaderStyles();
  const isSelected = selectedTab === resourceName;
  const { href, label, event } = TabsData[resourceName];
  const { data: session } = useSession();

  const [isHover, setIsHover] = React.useState(false);

  if (!session?.user || !checkAccess(session.user.role, session.user.effectivePermissions, resourceName)) {
    return null;
  }

  return (
    <Box
      height='100%'
      justifyContent='center'
      css={styles.tab}
      aria-label={isSelected ? 'selected-tab' : 'unselected-tab'}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      <Link href={href} passHref={true}>
        <Tab
          sx={{ width: '100%' }}
          label={label}
          {...a11yProps(resourceName + 1)}
          onClick={() => {
            logKariusEvent(EngagementEvents.nav_link_tapped, { link: event });
          }}
        />
      </Link>
      <Grow in={isSelected} timeout={isSelected ? 800 : 300}>
        <div data-test={`${href}-selected-indicator`} css={styles.indicator} />
      </Grow>
      <Grow in={!isSelected && isHover} timeout={isSelected ? 800 : 300}>
        <div css={styles.hoverIndicator} />
      </Grow>
    </Box>
  );
}
